import { resolveFieldKind } from './fieldKind';
import type { FieldDescriptor, JsonSchemaNode, UiSchema } from './types';

/**
 * Turns a JSON Schema object's `properties` into an ordered list of fields to
 * render. Order comes from `uiSchema.order` (a flat list of dot-paths), never
 * from the parsed object's own key order — `jsonb` does not preserve object
 * key order on round-trip (api/CONVENTIONS.md, M1-16, `app.mainlinepro` API
 * repo), so relying on it here would work in a dev sandbox and silently
 * reorder fields the moment a template round-trips through the real database.
 * A key with no `order` entry sorts after every ordered one, in whatever
 * order the browser's own JSON parse happens to produce — not deterministic,
 * and not meant to be; a template missing an order entry is missing one, not
 * merely inconvenienced.
 *
 * Pure function, no DOM/React dependency — this is the "framework-agnostic"
 * half of the renderer the AC asks for (portable to React Native's own
 * `useFormRenderer` later); only `fields/*.tsx` and `FormRenderer.tsx` are
 * web-specific.
 */
export function resolveFields(
  schema: JsonSchemaNode,
  uiSchema: UiSchema,
  parentPath = '',
): FieldDescriptor[] {
  const properties = schema.properties ?? {};
  const requiredKeys = new Set(schema.required ?? []);
  const orderIndex = new Map((uiSchema.order ?? []).map((path, index) => [path, index]));

  const keys = Object.keys(properties).sort((a, b) => {
    const pathA = childPath(parentPath, a);
    const pathB = childPath(parentPath, b);
    const rankA = orderIndex.get(pathA) ?? Number.MAX_SAFE_INTEGER;
    const rankB = orderIndex.get(pathB) ?? Number.MAX_SAFE_INTEGER;
    return rankA - rankB;
  });

  return keys.map((key) => {
    const path = childPath(parentPath, key);
    const propertySchema = properties[key]!;
    const hint = uiSchema.widgets?.[path];
    const kind = resolveFieldKind(propertySchema, hint);

    const descriptor: FieldDescriptor = {
      path,
      key,
      schema: propertySchema,
      required: requiredKeys.has(key),
      kind,
    };

    if (kind === 'section') {
      descriptor.children = resolveFields(propertySchema, uiSchema, path);
    } else if (kind === 'repeatingGroup' && propertySchema.items) {
      // Relative to a row, NOT prefixed with a row index — rows don't exist
      // yet. RepeatingGroupField re-roots these per row via remapFieldPath.
      descriptor.itemFields = resolveFields(propertySchema.items, uiSchema, '');
    }

    return descriptor;
  });
}

function childPath(parentPath: string, key: string): string {
  return parentPath ? `${parentPath}.${key}` : key;
}

/**
 * Re-roots a relative field descriptor (and its children, recursively) onto
 * an absolute path — how `RepeatingGroupField` turns one row's worth of
 * `itemFields` (computed once, relative) into the real fields for row N.
 */
export function remapFieldPath(field: FieldDescriptor, newPath: string): FieldDescriptor {
  return {
    ...field,
    path: newPath,
    children: field.children?.map((child) => remapFieldPath(child, `${newPath}.${child.key}`)),
  };
}
